import type {
  AdminFlowDefinition,
  AdminFlowDraftMessage,
  AdminFlowAutoBuilderProps,
} from "../../models/admin-flows.models";
import { proposeAdminFlow } from "../../services/admin-flows.service";
import { getAdminAgentErrorMessage } from "../../utils/admin-agent.utils";
import {
  ADMIN_FLOW_BUTTON,
  ADMIN_FLOW_INPUT,
  ADMIN_FLOW_PANEL,
  ADMIN_FLOW_PRIMARY,
} from "../../utils/admin-flows.constants";
import { readAdminFlowProposal, validateAdminFlow } from "../../utils/admin-flows.utils";
import AdminFlowWhiteboard from "./AdminFlowWhiteboard";
import { useEffect, useRef, useState } from "react";

// Generate a proposed configuration without granting the model permission to run tools or deliver outputs.
// Applying a proposal changes only this flow's draft; publication and execution remain separate actions.
const AdminFlowAutoBuilder = ({
  flow,
  tools,
  onChange,
  token,
  messages,
  onMessagesChange,
}: AdminFlowAutoBuilderProps) => {
  const [message, setMessage] = useState("");
  const [proposal, setProposal] = useState<AdminFlowDefinition>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);
  const pending = useRef(false);
  const currentRevision = useRef(flow.revision);
  const proposalRevision = useRef(flow.revision);
  currentRevision.current = flow.revision;

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const propose = async () => {
    if (!message.trim() || pending.current) return;

    pending.current = true;
    setBusy(true);
    setError("");
    setProposal(undefined);
    const instruction = message.trim();
    const transcript: AdminFlowDraftMessage[] = [
      ...messages,
      { id: crypto.randomUUID(), role: "user", content: instruction },
    ];
    onMessagesChange(transcript);
    const revision = flow.revision;

    try {
      const response = await proposeAdminFlow(token, flow, instruction);

      if (!mounted.current) return;
      if (currentRevision.current !== revision)
        throw new Error(
          "The flow changed while the proposal was being prepared. Request a new proposal for the current draft.",
        );

      const checked = readAdminFlowProposal(JSON.stringify(response.flow), flow);
      proposalRevision.current = revision;
      setProposal(checked);
      setMessage("");
      onMessagesChange([...transcript, { id: crypto.randomUUID(), role: "assistant", content: response.explanation }]);
    } catch (reason) {
      if (mounted.current) setError(getAdminAgentErrorMessage(reason));
    } finally {
      pending.current = false;

      if (mounted.current) setBusy(false);
    }
  };

  const apply = () => {
    if (!proposal) return;
    if (proposalRevision.current !== flow.revision) {
      setError("The draft changed. Request a fresh proposal before applying.");
      return;
    }

    onChange(proposal);
    setProposal(undefined);
  };

  return (
    <section
      data-test="admin-flow-auto-builder"
      className="grid min-h-[520px] gap-4 xl:grid-cols-[360px_minmax(0,1fr)]"
    >
      <div className={`${ADMIN_FLOW_PANEL} flex flex-col overflow-hidden`}>
        <header className="border-greyscale-20 border-b p-4">
          <h2 className="font-semibold">Describe your flow</h2>
          <p className="mt-1 text-xs opacity-60">Proposals change the draft only after you apply them.</p>
        </header>
        <div className="max-h-[480px] flex-1 space-y-3 overflow-y-auto p-4" aria-live="polite">
          {!messages.length && (
            <p className="text-sm opacity-60">
              Try “Find vessels that need attention, review their documents and prepare a short briefing.”
            </p>
          )}
          {messages.map((entry) => (
            <article
              key={entry.id}
              data-test={`admin-flow-auto-message-${entry.role}`}
              className={`rounded p-3 text-sm ${entry.role === "user" ? "ml-5 bg-[#E9F4F3] text-[#164b43]" : "border-greyscale-20 mr-5 border"}`}
            >
              <p className="mb-1 text-xs font-semibold">{entry.role === "user" ? "You" : "Flow assistant"}</p>
              <p className="whitespace-pre-wrap">{entry.content}</p>
            </article>
          ))}
          {busy && (
            <p role="status" className="text-sm opacity-60">
              Preparing a proposal…
            </p>
          )}
        </div>
        <form
          className="border-greyscale-20 space-y-3 border-t p-4"
          onSubmit={(event) => {
            event.preventDefault();
            void propose();
          }}
        >
          <label className="block text-xs font-medium" htmlFor="admin-flow-auto-instruction">
            What should this flow do?
          </label>
          <textarea
            id="admin-flow-auto-instruction"
            data-test="admin-flow-auto-instruction"
            maxLength={6000}
            rows={4}
            className={ADMIN_FLOW_INPUT}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="Describe a new flow or a change…"
          />
          {error && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}
          <button data-test="admin-flow-auto-propose" className={ADMIN_FLOW_PRIMARY} disabled={busy || !message.trim()}>
            Propose changes
          </button>
        </form>
      </div>
      <div className="min-w-0 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-semibold">{proposal ? "Proposed flow" : "Current flow"}</h2>
          {proposal && (
            <div className="flex gap-2">
              <button
                data-test="admin-flow-auto-reject"
                className={ADMIN_FLOW_BUTTON}
                onClick={() => setProposal(undefined)}
              >
                Discard proposal
              </button>
              <button data-test="admin-flow-auto-apply" className={ADMIN_FLOW_PRIMARY} onClick={apply}>
                Apply to draft
              </button>
            </div>
          )}
        </div>
        {proposal && (
          <p className="text-xs opacity-60">
            {proposal.nodes.length} blocks · {proposal.edges.length} connections ·{" "}
            {validateAdminFlow(proposal).length
              ? "Some settings still need attention before publishing."
              : "Ready to review and publish."}
          </p>
        )}
        <AdminFlowWhiteboard
          flow={proposal ?? flow}
          tools={tools}
          onChange={(changed) => (proposal ? setProposal(changed) : onChange(changed))}
        />
      </div>
    </section>
  );
};

export default AdminFlowAutoBuilder;
